import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserPreferences } from 'App/contexts/UserPreferencesContext';

function useToggleStateOnUser(stateKey, defaultIsVisible = true) {
    const { getPreference, setPreference, isLoading } = useUserPreferences();

    const [isOpen, setIsSideBarOpen] = useState(() => {
        const result = getPreference(stateKey, true);
        if (result == null) {
            return defaultIsVisible;
        }
        return result;
    });

    // Preferences load asynchronously, so the `useState` initializer above often
    // runs before they are available and falls back to `defaultIsVisible`. Once
    // loading finishes, sync the persisted value in — but only once, and never
    // after the user has toggled, so an in-flight user action is never clobbered.
    const hasSyncedRef = useRef(false);
    const hasUserToggledRef = useRef(false);
    useEffect(() => {
        if (isLoading || hasSyncedRef.current || hasUserToggledRef.current) {
            return;
        }
        hasSyncedRef.current = true;
        const stored = getPreference(stateKey, true);
        if (stored != null) {
            // Intentionally syncing the async-loaded preference into local state.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setIsSideBarOpen(stored);
        }
    }, [isLoading, getPreference, stateKey]);

    const toggle = useCallback(() => {
        hasUserToggledRef.current = true;
        setIsSideBarOpen((prev) => {
            const next = !prev;
            setPreference(stateKey, next);
            return next;
        });
    }, [stateKey, setPreference]);

    return [isOpen, toggle, isLoading];
}

export default useToggleStateOnUser;
